import { ArrowLeftOutlined, EnvironmentOutlined } from "@ant-design/icons"
import { Alert, Breadcrumb, Button, Progress, Skeleton, Tabs, Tag } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Link, Navigate, useNavigate, useParams } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { getProgressColor } from "../../Projects/constants/project-progress.ts"
import { PortalPageContainer } from "../components/PortalPageContainer.tsx"
import { formatPortalDate } from "../helpers/format-date.ts"
import {
  fetchPortalProjectDetail,
  fetchPortalProjects
} from "../redux/portal.thunks.ts"
import { ProjectOverviewTab } from "./ProjectOverviewTab.tsx"
import { ProjectMessagesTab } from "./ProjectMessagesTab.tsx"
import { ProjectTimelineTab } from "./ProjectTimelineTab.tsx"

const TABS = ["overview", "timeline", "messages"] as const
type WorkspaceTab = (typeof TABS)[number]

// A project workspace used to host every module (Documents, Photos,
// Calendar, Messages, Payments) as its own nested tab. Those five now live
// only at their global route, filtered by `?project=` — this map is what
// keeps an old `/portal/projects/:id/<module>` bookmark, deep link or
// notification target landing on the right place instead of a 404 or a
// silent fallback to Overview.
const LEGACY_TAB_REDIRECTS: Record<string, string> = {
  documents: RoutesPaths.portal.documents,
  photos: RoutesPaths.portal.photos,
  calendar: RoutesPaths.portal.calendar,
  // `messages` is a real tab again — a customer's conversation belongs to the
  // project it is about, so it lives in that project's workspace.
  payments: RoutesPaths.portal.payments
}

/** A project's persistent workspace: breadcrumb, header (name, company,
 * status, progress/phase/completion — the same figures the GC app shows),
 * and a two-tab strip (Overview | Timeline). Every other module a project
 * used to host as its own tab now lives at its global route, opened
 * pre-filtered to this project via `?project=` rather than duplicated
 * here — see `LEGACY_TAB_REDIRECTS` for the deep-link compatibility this
 * replaces. */
function PortalProjectWorkspace() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { id, tab } = useParams<{ id: string; tab?: string }>()

  const projects = useAppSelector((state) => state.portal.projects)
  const projectsLoaded = useAppSelector((state) => state.portal.projectsLoaded)
  const detail = useAppSelector((state) => state.portal.projectDetail)
  const detailFetching = useAppSelector(
    (state) => state.portal.projectDetailFetching
  )
  const detailFailed = useAppSelector(
    (state) => state.portal.projectDetailError
  )

  useEffect(() => {
    if (!projectsLoaded) void dispatch(fetchPortalProjects())
  }, [dispatch, projectsLoaded])

  useEffect(() => {
    if (id) void dispatch(fetchPortalProjectDetail(id))
  }, [dispatch, id])

  if (!id) return <Navigate to={RoutesPaths.portal.projects} replace />

  if (tab && LEGACY_TAB_REDIRECTS[tab]) {
    return (
      <Navigate to={`${LEGACY_TAB_REDIRECTS[tab]}?project=${id}`} replace />
    )
  }

  const activeTab: WorkspaceTab = TABS.includes(tab as WorkspaceTab)
    ? (tab as WorkspaceTab)
    : "overview"

  // A stale/unknown tab segment (typo'd deep link, an old bookmark) lands on
  // Overview rather than a blank page — but a genuinely *unknown project id*
  // (not in the caller's own authorized list once loaded) is a real
  // revoked-access case, not a routing nicety, so that one 404s instead.
  if (projectsLoaded && !projects.some((p) => p.id === id)) {
    return (
      <PortalPageContainer className="max-w-3xl">
        <Alert
          type="warning"
          showIcon
          message={t("portal.project.notAuthorized")}
          action={
            <Button
              size="small"
              onClick={() => navigate(RoutesPaths.portal.projects)}
            >
              {t("portal.project.backToProjects")}
            </Button>
          }
        />
      </PortalPageContainer>
    )
  }

  return (
    <>
      <PortalPageContainer className="pb-0">
        <Breadcrumb
          className="mb-3"
          items={[
            {
              title: (
                <Link to={RoutesPaths.portal.projects}>
                  {t("portal.projects.title")}
                </Link>
              )
            },
            { title: detail?.lead_name ?? "…" }
          ]}
        />

        {!detail && detailFetching && (
          <Skeleton active paragraph={{ rows: 3 }} />
        )}

        {detailFailed && !detail && (
          <Alert
            type="error"
            showIcon
            message={t("portal.project.loadError")}
            action={
              <Button
                size="small"
                onClick={() => void dispatch(fetchPortalProjectDetail(id))}
              >
                {t("portal.home.retry")}
              </Button>
            }
          />
        )}

        {detail && (
          <div className="mb-4 flex flex-col gap-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <Button
                  type="link"
                  icon={<ArrowLeftOutlined />}
                  className="!px-0"
                  onClick={() => navigate(RoutesPaths.portal.projects)}
                >
                  {t("portal.project.backToProjects")}
                </Button>
                <h1 className="text-[24px] font-semibold leading-tight text-gray-900">
                  {detail.lead_name}
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                  {detail.gcc_name && (
                    <span className="font-medium text-gray-700">
                      {detail.gcc_name}
                    </span>
                  )}
                  {detail.address && (
                    <span className="flex items-center gap-1">
                      <EnvironmentOutlined />
                      {[detail.address, detail.city].filter(Boolean).join(", ")}
                    </span>
                  )}
                </div>
              </div>
              {detail.project_status_name && (
                <Tag color="blue" className="mt-1">
                  {detail.project_status_name}
                </Tag>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-100 bg-white px-5 py-4 sm:grid-cols-3">
              <div>
                <p className="text-xs uppercase text-gray-400">
                  {t("portal.project.progressLabel")}
                </p>
                {typeof detail.progress_percent === "number" ? (
                  <Progress
                    percent={detail.progress_percent}
                    size="small"
                    strokeColor={getProgressColor(detail.progress_percent)}
                    className="mt-1 max-w-[220px]"
                  />
                ) : (
                  <p className="mt-1 text-sm text-gray-800">
                    {t("portal.home.noEstimateYet")}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs uppercase text-gray-400">
                  {t("portal.home.currentPhase")}
                </p>
                <p className="mt-1 text-sm text-gray-800">
                  {detail.phase || t("portal.home.noPhaseInProgress")}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-400">
                  {t("portal.home.estimatedCompletion")}
                </p>
                <p className="mt-1 text-sm text-gray-800">
                  {detail.end_date
                    ? formatPortalDate(detail.end_date)
                    : t("portal.home.noEstimateYet")}
                </p>
              </div>
            </div>
          </div>
        )}

        <Tabs
          activeKey={activeTab}
          onChange={(key) =>
            navigate(
              RoutesPaths.portal.project(
                id,
                key === "overview" ? undefined : key
              )
            )
          }
          items={TABS.map((key) => ({
            key,
            label: t(`portal.project.tabs.${key}`)
          }))}
        />
      </PortalPageContainer>

      {activeTab === "overview" && <ProjectOverviewTab projectId={id} />}
      {activeTab === "timeline" && <ProjectTimelineTab projectId={id} />}
      {activeTab === "messages" && <ProjectMessagesTab projectId={id} />}
    </>
  )
}

export default PortalProjectWorkspace
